import { SystemHealth, Typography } from '@amway/react-components';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Col, Container, Row } from 'react-bootstrap';
import ExecutionsOverTimeStackedChartComponent from '../../components/charts/executions-over-time-stacked-chart';
import ReportsSortedBarChartComponent from '../../components/charts/reports-sorted-bar-chart';
import StepErrorsSortedBarChartComponent from '../../components/charts/step-errors-sorted-bar-chart';
import TealiumDataLayer from '../../components/hocs/tealium-data-layer';
import { Item } from '../../components/ui/dropdown-btn';
import IconButton from '../../components/ui/icon-btn';
import { Features } from '../../config/features';
import { WithFeaturesProxy } from '../../helpers/with-features-proxy';
import { ChartOption, ReportsSortedBarChartData } from '../../interface/reports';
import { ExecutionsOverTime } from '../../resources/executions-over-time/executions-over-time-model';
import useMetrics from '../../resources/metrics/metrics-hook';
import useProcessors from '../../resources/processors/processors-hook';
import { StepErrors } from '../../resources/step-errors/step-errors-model';
import useSystemsHealth from '../../resources/systems-health/systems-health-hook';
import adminService from '../../service/admin.service';
import { subtractDays, toISOString } from '../../utils/date-utils';
import DashboardMetricsComponent from './dashboard-metrics';
import DashboardMetricsFormComponent from './dashboard-metrics-form';
import './index.scss';
import SystemEnvironmentHealth from './system-environment-health';

interface ExposedProps {}

interface Props extends ExposedProps {
  hasAmwayProfile: boolean;
  hasExponea: boolean;
  hasOrderLookupAPI: boolean;
  hasProductInfoAPI: boolean;
  hasVolumeAPI: boolean;
  hasUplines: boolean;
  hasDocgen: boolean;
  hasPublisherAPI: boolean;
  hasNgcOrderDetailsApi: boolean;
  hasNgcReturnDetailsApi: boolean;
  hasNgcAccountDetailsApi: boolean;
  hasNgcAccountMappingsApi: boolean;
  hasNgcAccountsByProfileIdApi: boolean;
  hasNgcPaymentsApi: boolean;
  hasNgcProfileByPartyIdApi: boolean;
  hasNgcProfileApi: boolean;
  hasNgcUserApi: boolean;
  hasNgcAccountEmailsApi: boolean;
  hasNgcAccountPhonesApi: boolean;
  hasNgcAnonymousDeliveryTrackingApi: boolean;
}

function DashboardComponent({
  hasAmwayProfile,
  hasExponea,
  hasOrderLookupAPI,
  hasProductInfoAPI,
  hasVolumeAPI,
  hasUplines,
  hasDocgen,
  hasPublisherAPI,
  hasNgcOrderDetailsApi,
  hasNgcReturnDetailsApi,
  hasNgcAccountDetailsApi,
  hasNgcAccountMappingsApi,
  hasNgcAccountsByProfileIdApi,
  hasNgcPaymentsApi,
  hasNgcProfileByPartyIdApi,
  hasNgcProfileApi,
  hasNgcUserApi,
  hasNgcAccountEmailsApi,
  hasNgcAccountPhonesApi,
  hasNgcAnonymousDeliveryTrackingApi,
}: Props) {
  const { systemsHealth, fetchSystemsHealth } = useSystemsHealth();
  const { processors: processorsData, fetchProcessors } = useProcessors();
  const { metrics, fetchMetrics } = useMetrics();
  const [maxDate] = useState<string>(toISOString(new Date()));
  const [startDate, setStartDate] = useState<string>(toISOString(subtractDays(new Date(), 1)));
  const [endDate, setEndDate] = useState<string>(maxDate);
  const [lastUpdate, setLastUpdate] = useState<string>(new Date().toLocaleString());
  const [executionReports, setExecutionReports] = useState<ReportsSortedBarChartData[]>();
  const [executionsOverTime, setExecutionsOverTime] = useState<ExecutionsOverTime>();
  const [loading, setLoading] = useState<boolean>(false);
  const [activeChart, setActiveChart] = useState<Item | undefined | null>();
  const [stepErrors, setStepErrors] = useState<StepErrors[]>();
  const [dateAggregation, setDateAggregation] = useState<Item | undefined>({ id: 'DAY', label: 'DAY' });

  const otherIndicatorsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchSystemsHealth();
  }, [fetchSystemsHealth]);

  const processors = useMemo(() => {
    return (processorsData.data ?? []).map(({ name }) => name);
  }, [processorsData.data]);

  const loadChartData = useCallback(
    (e?: any) => {
      if (startDate && endDate) {
        e && e.preventDefault();
        setLoading(true);
        if (activeChart?.id === ChartOption[ChartOption.EXECUTIONS_BAR_CHART]) {
          adminService
            .getExecutionReports({
              startDate: new Date(startDate + ' 00:00:00.000'),
              endDate: new Date(endDate + ' 23:59:59.999'),
              filters: { processor: processors },
              grouping: 'Processor',
            })
            .then(setExecutionReports)
            .finally(() => setLoading(false));
        } else if (activeChart?.id === ChartOption[ChartOption.EXECUTIONS_OVER_TIME] && dateAggregation) {
          adminService
            .getExecutionsOverTimeData({
              startDate: new Date(startDate + ' 00:00:00.000'),
              endDate: new Date(endDate + ' 23:59:59.999'),
              dateAggregation: dateAggregation.label,
            })
            .then(setExecutionsOverTime)
            .finally(() => setLoading(false));
        } else if (activeChart?.id === ChartOption[ChartOption.PROCESSOR_STEP_ERRORS]) {
          adminService
            .getStepErrors({
              startDate: new Date(startDate + ' 00:00:00.000'),
              endDate: new Date(endDate + ' 23:59:59.999'),
            })
            .then(setStepErrors)
            .finally(() => setLoading(false));
        }
        fetchMetrics(startDate + ' 00:00:00.000', endDate + ' 23:59:59.999');
      }
    },
    [startDate, endDate, activeChart?.id, dateAggregation, fetchMetrics, processors],
  );

  const loadIndicators = useCallback(() => {
    fetchSystemsHealth();
    setLastUpdate(new Date().toLocaleString());
  }, [fetchSystemsHealth]);

  useEffect(() => {
    if (!metrics.data) {
      fetchProcessors();
    }
  }, [metrics.data, fetchProcessors]);

  useEffect(() => {
    fetchMetrics(startDate + ' 00:00:00.000', endDate + ' 23:59:59.999');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadIndicators();
  }, [loadIndicators]);

  const checkExistanceOtherIndicators = () => {
    if (otherIndicatorsContainerRef.current && otherIndicatorsContainerRef.current !== undefined) {
      return otherIndicatorsContainerRef.current?.children.length > 0;
    }
    return false;
  };

  return (
    <TealiumDataLayer
      page_name="Dashboard"
      page_section="metrics"
      page_category="Metrics"
      page_subCategory="System Health and Usage">
      <Container className="dashboard">
        <Card>
          <Card.Body>
            <Row className="title">
              <Col md={10}>
                <Typography variant="heading">System Health</Typography>
                <Typography weight="bold" color="text-gray">
                  Last update on {lastUpdate}
                </Typography>
              </Col>
              <Col md={2} className="d-flex justify-content-end">
                <IconButton onClick={loadIndicators} styleType="link">
                  REFRESH
                </IconButton>
              </Col>
            </Row>
            <Row className={`indicators ${checkExistanceOtherIndicators() && 'separator'}`}>
              <Col md={12}>
                <Typography variant="captionMedium" color="primary" weight="italic">
                  Self indicators
                </Typography>
              </Col>
              <Col md={4}>
                <SystemHealth name="API" status={systemsHealth.self.data} />
              </Col>
              {hasPublisherAPI && (
                <Col md={4}>
                  <SystemHealth name="Publisher API" status={systemsHealth.publisherApi.data} />
                </Col>
              )}
            </Row>
            <Row className="indicators" ref={otherIndicatorsContainerRef}>
              {checkExistanceOtherIndicators() && (
                <Col md={12}>
                  <Typography variant="captionMedium" color="primary" weight="italic">
                    Other indicators
                  </Typography>
                </Col>
              )}
              {hasAmwayProfile && (
                <Col md={4}>
                  <SystemHealth name="Amway Profile" status={systemsHealth.amwayProfile.data} />
                </Col>
              )}
              {hasExponea && (
                <Col md={4}>
                  <SystemHealth name="Exponea" status={systemsHealth.exponea.data} />
                </Col>
              )}
              {hasOrderLookupAPI && (
                <Col md={4}>
                  <SystemHealth name="Order Lookup API" status={systemsHealth.orderApi.data} />
                </Col>
              )}
              {hasProductInfoAPI && (
                <Col md={4}>
                  <SystemHealth name="Product Info API" status={systemsHealth.itemApi.data} />
                </Col>
              )}
              {hasVolumeAPI && (
                <Col md={4}>
                  <SystemHealth name="Volume API" status={systemsHealth.volumeApi.data} />
                </Col>
              )}
              {hasUplines && (
                <Col md={4}>
                  <SystemHealth name="Uplines" status={systemsHealth.uplines.data} />
                </Col>
              )}
              {hasDocgen && (
                <Col md={4}>
                  <SystemHealth name="Docgen" status={systemsHealth.docgen.data} />
                </Col>
              )}
              {hasNgcOrderDetailsApi && (
                <Col md={4}>
                  <SystemEnvironmentHealth
                    name="NGC Order Details API"
                    environments={systemsHealth.ngcOrderDetailsApi.data}
                  />
                </Col>
              )}
              {hasNgcReturnDetailsApi && (
                <Col md={4}>
                  <SystemEnvironmentHealth
                    name="NGC Return Details API"
                    environments={systemsHealth.ngcReturnDetailsApi.data}
                  />
                </Col>
              )}
              {hasNgcAccountDetailsApi && (
                <Col md={4}>
                  <SystemEnvironmentHealth
                    name="NGC Account Details API"
                    environments={systemsHealth.ngcAccountDetailsApi.data}
                  />
                </Col>
              )}
              {hasNgcAccountMappingsApi && (
                <Col md={4}>
                  <SystemEnvironmentHealth
                    name="NGC Account Mappings API"
                    environments={systemsHealth.ngcAccountMappingsApi.data}
                  />
                </Col>
              )}
              {hasNgcAccountsByProfileIdApi && (
                <Col md={4}>
                  <SystemEnvironmentHealth
                    name="NGC Accounts by Profile ID API"
                    environments={systemsHealth.ngcAccountsByProfileIdApi.data}
                  />
                </Col>
              )}
              {hasNgcPaymentsApi && (
                <Col md={4}>
                  <SystemEnvironmentHealth name="NGC Payments API" environments={systemsHealth.ngcPaymentsApi.data} />
                </Col>
              )}
              {hasNgcProfileByPartyIdApi && (
                <Col md={4}>
                  <SystemEnvironmentHealth
                    name="NGC Profile by Party ID API"
                    environments={systemsHealth.ngcProfileByPartyIdApi.data}
                  />
                </Col>
              )}
              {hasNgcProfileApi && (
                <Col md={4}>
                  <SystemEnvironmentHealth name="NGC Profile API" environments={systemsHealth.ngcProfileApi.data} />
                </Col>
              )}
              {hasNgcUserApi && (
                <Col md={4}>
                  <SystemEnvironmentHealth name="NGC User API" environments={systemsHealth.ngcUserApi.data} />
                </Col>
              )}
              {hasNgcAccountEmailsApi && (
                <Col md={4}>
                  <SystemEnvironmentHealth
                    name="NGC Account Emails API"
                    environments={systemsHealth.ngcAccountEmailsApi.data}
                  />
                </Col>
              )}
              {hasNgcAccountPhonesApi && (
                <Col md={4}>
                  <SystemEnvironmentHealth
                    name="NGC Account Phones API"
                    environments={systemsHealth.ngcAccountPhonesApi.data}
                  />
                </Col>
              )}
              {hasNgcAnonymousDeliveryTrackingApi && (
                <Col md={4}>
                  <SystemEnvironmentHealth
                    name="NGC Anonymous Delivery Tracking API"
                    environments={systemsHealth.ngcAnonymousDeliveryTrackingApi.data}
                  />
                </Col>
              )}
            </Row>
          </Card.Body>
        </Card>
        <Card>
          <Card.Body>
            <Row className="title">
              <Col>
                <Typography variant="heading">Data and Reports</Typography>
                <Typography weight="bold" color="text-gray" className="mt-3">
                  Core metrics from the ACDP system. Select the time periods below to update the date range input.
                </Typography>
              </Col>
            </Row>
            <DashboardMetricsFormComponent
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              maxDate={maxDate}
              submit={loadChartData}
              loading={loading}
              chartOption={activeChart}
              setChartOption={setActiveChart}
              dateAggregation={dateAggregation}
              setDateAggregation={setDateAggregation}
              loadChartData={loadChartData}
            />
            <DashboardMetricsComponent metrics={metrics.data} />
            {processors.length > 0 && (
              <ReportsSortedBarChartComponent
                data={executionReports ?? []}
                id="executions-report-chart-dashboard"
                activeChart={activeChart}
              />
            )}
            {executionsOverTime && (
              <ExecutionsOverTimeStackedChartComponent
                data={executionsOverTime}
                id="executions-over-time-stacked-chart"
                activeChart={activeChart}
              />
            )}
            {stepErrors && (
              <StepErrorsSortedBarChartComponent
                data={stepErrors ?? []}
                id="step-errors-sorted-bar-chart"
                activeChart={activeChart}
              />
            )}
          </Card.Body>
        </Card>
      </Container>
    </TealiumDataLayer>
  );
}

export default WithFeaturesProxy<ExposedProps>(
  Features.MktFeature_Health_AmwayProfile,
  Features.MktFeature_Health_Exponea,
  Features.MktFeature_Health_OrderLookupAPI,
  Features.MktFeature_Health_ProductInfoAPI,
  Features.MktFeature_Health_VolumeAPI,
  Features.MktFeature_Health_Uplines,
  Features.MktFeature_Health_Docgen,
  Features.MktFeature_Health_PublisherAPI,
  Features.MktFeature_Health_NGCOrderDetailAPI,
  Features.MktFeature_Health_NGCReturnDetailAPI,
  Features.MktFeature_Health_NGCAccountDetailsAPI,
  Features.MktFeature_Health_NGCAccountMappingsAPI,
  Features.MktFeature_Health_NGCAccountsByProfileIdAPI,
  Features.MktFeature_Health_NGCPaymentsAPI,
  Features.MktFeature_Health_NGCProfileByPartyIdAPI,
  Features.MktFeature_Health_NGCProfileAPI,
  Features.MktFeature_Health_NGCUserAPI,
  Features.MktFeature_Health_NGCAccountEmailsAPI,
  Features.MktFeature_Health_NGCAccountPhonesAPI,
  Features.MktFeature_Health_NGCAnonymousDeliveryTrackingAPI,
)((props, ...hasFeatures) => {
  const [
    hasAmwayProfile,
    hasExponea,
    hasOrderLookupAPI,
    hasProductInfoAPI,
    hasVolumeAPI,
    hasUplines,
    hasDocgen,
    hasPublisherAPI,
    hasNgcOrderDetailsApi,
    hasNgcReturnDetailsApi,
    hasNgcAccountDetailsApi,
    hasNgcAccountMappingsApi,
    hasNgcAccountsByProfileIdApi,
    hasNgcPaymentsApi,
    hasNgcProfileByPartyIdApi,
    hasNgcProfileApi,
    hasNgcUserApi,
    hasNgcAccountEmailsApi,
    hasNgcAccountPhonesApi,
    hasNgcAnonymousDeliveryTrackingApi,
  ] = hasFeatures;
  return (
    <DashboardComponent
      {...props}
      hasAmwayProfile={hasAmwayProfile}
      hasExponea={hasExponea}
      hasOrderLookupAPI={hasOrderLookupAPI}
      hasProductInfoAPI={hasProductInfoAPI}
      hasVolumeAPI={hasVolumeAPI}
      hasUplines={hasUplines}
      hasDocgen={hasDocgen}
      hasPublisherAPI={hasPublisherAPI}
      hasNgcOrderDetailsApi={hasNgcOrderDetailsApi}
      hasNgcReturnDetailsApi={hasNgcReturnDetailsApi}
      hasNgcAccountDetailsApi={hasNgcAccountDetailsApi}
      hasNgcAccountMappingsApi={hasNgcAccountMappingsApi}
      hasNgcAccountsByProfileIdApi={hasNgcAccountsByProfileIdApi}
      hasNgcPaymentsApi={hasNgcPaymentsApi}
      hasNgcProfileByPartyIdApi={hasNgcProfileByPartyIdApi}
      hasNgcProfileApi={hasNgcProfileApi}
      hasNgcUserApi={hasNgcUserApi}
      hasNgcAccountEmailsApi={hasNgcAccountEmailsApi}
      hasNgcAccountPhonesApi={hasNgcAccountPhonesApi}
      hasNgcAnonymousDeliveryTrackingApi={hasNgcAnonymousDeliveryTrackingApi}
    />
  );
});
